import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import SessionInfo from './SessionInfo';
import SessionActions from './SessionActions';
import SessionStatus from './SessionStatus';
import SessionAttendanceActions from './SessionAttendanceActions';
import CapacityIndicator from './CapacityIndicator';

interface TrainingSession {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
  max_participants: number | null;
  instructor_id: string | null;
  created_at: string;
  attendance_count?: number;
  status?: 'scheduled' | 'completed' | 'cancelled';
}

interface PromotionSession {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
  max_participants: number | null;
  created_by: string | null;
  created_at: string;
}

interface SessionCardProps {
  session: (TrainingSession | PromotionSession) & { type: 'training' | 'promotion' };
  userRole: string | undefined;
  onEdit?: (session: TrainingSession) => void;
  onDelete?: (sessionId: string) => void;
  onNotify?: (session: TrainingSession) => void;
  onConfirmAttendance?: (session: TrainingSession) => void;
  onConfirmPromotionAttendance?: (sessionId: string, willAttend: boolean) => void;
  isSelected?: boolean;
  onSelect?: (sessionId: string, checked: boolean) => void;
}

const SessionCard: React.FC<SessionCardProps> = ({
  session,
  userRole,
  onEdit,
  onDelete,
  onNotify,
  onConfirmAttendance,
  onConfirmPromotionAttendance,
  isSelected = false,
  onSelect
}) => {
  useUiLanguage();
  const { profile } = useAuth();
  const [showAttendance, setShowAttendance] = useState(false);
  const [userResponse, setUserResponse] = useState<boolean | null>(null);

  const fetchUserResponse = async () => {
    if (!profile || session.type !== 'training') return;

    try {
      const { data, error } = await supabase
        .from('session_attendance')
        .select('attended')
        .eq('session_id', session.id)
        .eq('student_id', profile.id)
        .maybeSingle();

      if (error) throw error;
      setUserResponse(data?.attended || null);
    } catch (error) {
      console.error('Error fetching user response:', error);
    }
  };

  const handleAttendanceResponse = async (willAttend: boolean) => {
    if (!profile || session.type !== 'training') return;

    try {
      const { data: existingResponse } = await supabase
        .from('session_attendance')
        .select('id')
        .eq('session_id', session.id)
        .eq('student_id', profile.id)
        .maybeSingle();

      if (existingResponse) {
        const { error } = await supabase
          .from('session_attendance')
          .update({ attended: willAttend })
          .eq('id', existingResponse.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('session_attendance')
          .insert({
            session_id: session.id,
            student_id: profile.id,
            attended: willAttend
          });

        if (error) throw error;
      }

      setUserResponse(willAttend);
      toast({
        title: uiText("Response Recorded"),
        description: uiText("Your attendance response has been recorded: {{value0}}", { value0: willAttend ? uiText("Will attend") : uiText("Will not attend") })
      });
    } catch (error) {
      console.error('Error recording attendance response:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to record your response. Please try again."),
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    if (userRole === 'student') {
      fetchUserResponse();
    }
  }, [profile, session.id, userRole]);

  const trainingSession = session as TrainingSession;
  const isPastDate = new Date(session.date) < new Date();

  return (
    <div className="p-4 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
      <div className="flex flex-col gap-4">
        {/* Selection checkbox for admin and training sessions */}
        {(userRole === 'admin' || userRole === 'super_user') && session.type === 'training' && onSelect && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`select-${session.id}`}
              checked={isSelected}
              onCheckedChange={(checked) => onSelect(session.id, checked as boolean)}
            />
            <label htmlFor={`select-${session.id}`} className="text-sm font-medium">
              {uiText("Select for bulk action")}</label>
          </div>
        )}

        {/* Session Info Section */}
        <div className="flex-1 min-w-0">
          <SessionInfo
            session={session}
            userRole={userRole}
            userResponse={userResponse}
          />
          <div className="mt-2">
            {session.type === 'training' && (
              <SessionStatus
                status={trainingSession.status}
                isPastDate={isPastDate}
              />
            )}
          </div>
        </div>

        {/* Actions Section */}
        <div className="flex-shrink-0">
          <SessionActions
            session={session}
            userRole={userRole}
            userResponse={userResponse}
            showAttendance={showAttendance}
            onEdit={onEdit}
            onDelete={onDelete}
            onNotify={onNotify}
            onToggleAttendance={() => setShowAttendance(!showAttendance)}
            onAttendanceResponse={handleAttendanceResponse}
            onConfirmPromotionAttendance={onConfirmPromotionAttendance}
          />
        </div>
      </div>

      {/* Student Attendance Table for Admins */}
      <SessionAttendanceActions
        sessionId={session.id}
        sessionType={session.type}
        userRole={userRole}
        showAttendance={showAttendance}
      />

      {/* Participation indicator */}
      {session.max_participants && session.type === 'training' && (
        <CapacityIndicator
          attendanceCount={(session as TrainingSession).attendance_count || 0}
          maxParticipants={session.max_participants}
        />
      )}
    </div>
  );
};

export default SessionCard;
